<script setup>
// 자사몰 공통 푸터 — 퍼블리싱 밖 화면(AppLayout 이 감싸는 상품·장바구니·약관 등).
//
// 메인(MallHomeBody / MallHomeBodyMobile 의 푸터)과 같은 모습으로 맞춘 것이다.
// 예전에는 이 푸터만 밝은 회색이라 메인에서 상품 목록으로 넘어가면 아래가 통째로
// 다른 사이트처럼 보였다.
//
// 메인 쪽은 Figma 고정 캔버스(1920 / 375)를 등비 스케일한 생성물이지만
// 이 화면들은 보통의 반응형이라, 같은 값을 폭에 따라 나눠 쓴다.
//   Figma 307:7187 (1920 x 411) → lg: 이상
//   Figma 2004:1559 (375 x 617) → 그 아래
// 글자 크기·투명도·간격이 두 쪽에서 다르다. 예컨대 회사 정보는 데스크톱이 흰색
// 그대로이고 모바일은 60%다. 퍼블리싱을 그대로 따랐다.
//
// 메뉴는 상단 메뉴와 같은 표를 쓴다(data/siteMenu.js). 약관·방침은 자사몰에만
// 있고 같은 앱 안의 화면이라 router-link 로 넘긴다.
import { SITE_MENU } from '@/data/siteMenu.js'
import { FOOTER_LEGAL } from '@/composables/useSiteFooter.js'
import imgSocialYouTube from '@/assets/figma/ba2e5ba040ce51d33a1623b513a3690e8a7fadfc.svg'
import imgSocialFacebook from '@/assets/figma/91db2067d123c43be29c48e9fe466bb2eff3a7a1.svg'
import imgSocialX from '@/assets/figma/66a12c4d4c88975ee20c830c0c8b6b31256fa7f9.svg'
import imgSocialInstagram from '@/assets/figma/a46be4fab735b5f3c3c3f0967ecf2961c7e444a2.svg'

// 위 메뉴 줄은 다른 사이트와 같은 다섯 칸만 둔다.
// 법적 고지는 아래 회사정보 밑으로 내렸다(2026-09-28 지시).
const menu = SITE_MENU

// 회사 정보. 한 줄 안의 조각들은 세로 구분선으로 이어 붙인다.
// 퍼블리싱은 데스크톱에서 두 칸으로 나눠 놓았고 모바일은 한 줄로 이어진다.
const INFO_LEFT = [
  ['AURA', 'CEO', '배재한'],
  ['Business Registration No.', '573-81-03483'],
  ['Email', 'goldnara4@gmail.com'],
]
const INFO_RIGHT = [
  ['Address', '서울 종로구 돈화문로5가길 1 피카디리플러스 7층 701호'],
  ['골드나라 배재한의 금토크', '경남 창원시 성산구 원이대로 320 시티세븐 W145'],
  ['Number', '1833-5977'],
]

const SOCIALS = [
  { key: 'youtube', alt: '유튜브', src: imgSocialYouTube },
  { key: 'facebook', alt: '페이스북', src: imgSocialFacebook },
  { key: 'x', alt: 'X', src: imgSocialX },
  { key: 'instagram', alt: '인스타그램', src: imgSocialInstagram },
]

// 개인정보처리방침만 다른 색으로 둔다 — useSiteFooter 의 strong 과 같은 규칙이다.
// 상단 메뉴가 현재 화면을 표시할 때 쓰는 금색과 같다(2026-09-28 지시).
// 검정 바탕에서 대비 6.9:1 이라 또렷하게 읽힌다.
const linkClass = (item) =>
  item.strong ? 'font-bold text-[#c4890b]' : 'transition-colors hover:text-white/70'
</script>

<template>
  <footer class="w-full bg-black text-white">
    <div class="mx-auto w-full max-w-[1280px] px-[20px] py-[40px] lg:py-[50px]">
      <!-- 안내문 + 메뉴. 퍼블리싱은 데스크톱에서 좌우로 나란히, 모바일에서 위아래다. -->
      <div class="flex flex-col gap-[20px] lg:flex-row lg:items-start lg:justify-between lg:gap-[40px]">
        <p class="text-[13px] font-normal leading-[18px] lg:text-[18px] lg:leading-[26px]">
          실물 금 자산 거래부터 미디어, 기업 협력,실시간 거래 플랫폼까지
          <br aria-hidden />
          골드나라의 주요 서비스를 확인해보세요.
        </p>

        <!-- 메뉴 칸 상한이 560 이면 다섯 칸이 두 줄로 접힌다(REAL GOLD TRADE 가
             내려간다). 푸터 폭을 헤더와 같은 1530 으로 맞추면서 안쪽이 1490 이
             됐고, 왼쪽 안내문 480 을 빼도 970 이 남아 한 줄에 들어간다. -->
        <nav
          class="flex flex-wrap gap-x-[15px] gap-y-[8px] text-[14px] font-semibold leading-[20px] lg:max-w-[700px] lg:justify-end lg:gap-x-[28px] lg:text-[18px] lg:leading-[26px]"
        >
          <template v-for="item in menu" :key="item.key">
            <router-link v-if="item.to" :to="item.to" :class="linkClass(item)">
              {{ item.label }}
            </router-link>
            <a v-else :href="item.href" :class="linkClass(item)">{{ item.label }}</a>
          </template>
        </nav>
      </div>

      <div class="my-[20px] h-px w-full bg-white/10 lg:my-[30px] lg:bg-white/30" />

      <!-- 회사 정보 -->
      <div class="flex flex-col gap-[8px] lg:flex-row lg:gap-[56px]">
        <div
          v-for="(block, b) in [INFO_LEFT, INFO_RIGHT]"
          :key="b"
          class="flex flex-col gap-[8px] lg:gap-[6px]"
        >
          <div v-for="(row, r) in block" :key="r" class="flex flex-wrap items-center gap-x-[6px] gap-y-[2px] lg:gap-x-[10px]">
            <template v-for="(part, i) in row" :key="i">
              <span v-if="i > 0" aria-hidden class="h-[10px] w-px shrink-0 bg-white/30 lg:h-[12px]" />
              <span class="text-[12px] font-normal leading-[16px] text-white/60 lg:text-[15px] lg:leading-[22px] lg:text-white">
                {{ part }}
              </span>
            </template>
          </div>
        </div>
      </div>

      <!-- 법적 고지. 회사정보 바로 아래가 국내 상거래 사이트의 흔한 자리다.
           위 메뉴 줄에 같이 두면 일곱 칸이 되어 왼쪽 안내문과 부딪친다
           (2026-09-28 지시). 메인 푸터(MallFooter)와 같은 차림이다. -->
      <div class="mt-[14px] flex flex-wrap items-center gap-[10px] lg:mt-[16px]">
        <template v-for="(item, i) in FOOTER_LEGAL" :key="item.key">
          <span v-if="i > 0" aria-hidden class="h-[10px] w-px shrink-0 bg-white/30 lg:h-[12px]" />
          <router-link
            :to="item.to"
            class="text-[12px] leading-[16px] lg:text-[15px] lg:leading-[22px]"
            :class="item.strong ? 'font-bold text-[#c4890b]' : 'text-white/60 lg:text-white'"
          >
            {{ item.label }}
          </router-link>
        </template>
      </div>

      <!-- 저작권 + 소셜. 퍼블리싱은 데스크톱에서 좌우 끝, 모바일에서 위아래다. -->
      <div
        class="mt-[24px] flex flex-col gap-[16px] lg:mt-[30px] lg:flex-row lg:items-center lg:justify-between"
      >
        <p class="text-[12px] font-normal leading-[16px] text-white/30 lg:text-[14px] lg:leading-[20px] lg:text-white/50">
          Copyright © AURA. All rights reserved.
        </p>
        <div class="flex items-start gap-[20px] lg:gap-[24px]">
          <img
            v-for="s in SOCIALS"
            :key="s.key"
            :alt="s.alt"
            :src="s.src"
            class="size-[24px] shrink-0 lg:size-[30px]"
          />
        </div>
      </div>
    </div>
  </footer>
</template>
